import { ComponentType, lazy } from "react";

// Each dialog is its own chunk: the forms, wallet and wheel are not needed for the first paint.
const Auth = lazy(() => import("@/views/Auth"));
const ForgotPassword = lazy(() => import("@/views/Auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/views/Auth/ResetPassword"));
const Terms = lazy(() => import("@/views/Auth/Terms"));
const TwoFactor = lazy(() => import("@/views/Auth/TwoFactor"));
const DailySpinDialog = lazy(() => import("@/views/Others/DailySpin"));
const WalletDialog = lazy(() => import("@/views/Profile/Wallet"));
const WalletSettingsDialog = lazy(() => import("@/views/Profile/Wallet/SettingsDialog"));

/** Fetches the sign-in dialog ahead of the click most guests make. */
export const preloadAuthDialog = () => void import("@/views/Auth");

export const DIALOG_NAMES = {
  AUTH: "auth",
  FORGOT_PASSWORD: "forgot-password",
  RESET_PASSWORD: "reset-password",
  TERMS: "terms",
  TWO_FA: "2fa",
  WALLET: "wallet",
  WALLET_SETTINGS: "wallet-settings",
  DAILY_SPIN: "daily-spin",
} as const;

type DialogType = (typeof DIALOG_NAMES)[keyof typeof DIALOG_NAMES];

export const DIALOG_COMPONENTS: Record<DialogType, ComponentType> = {
  [DIALOG_NAMES.AUTH]: Auth,
  [DIALOG_NAMES.FORGOT_PASSWORD]: ForgotPassword,
  [DIALOG_NAMES.RESET_PASSWORD]: ResetPassword,
  [DIALOG_NAMES.TERMS]: Terms,
  [DIALOG_NAMES.TWO_FA]: TwoFactor,
  [DIALOG_NAMES.WALLET]: WalletDialog,
  [DIALOG_NAMES.WALLET_SETTINGS]: WalletSettingsDialog,
  [DIALOG_NAMES.DAILY_SPIN]: DailySpinDialog,
};

export const DIALOG_MAX_WIDTH: Partial<Record<DialogType, "xs" | "sm" | "md" | "lg" | "xl">> = {
  [DIALOG_NAMES.AUTH]: "md",
  [DIALOG_NAMES.DAILY_SPIN]: "sm",
};

/**
 * A cap in pixels, for a dialog that wants a width between two breakpoints.
 *
 * Auth is the one: `sm` leaves the register form's two columns too narrow once
 * the mascot panel takes 300px, and the full `md` is wider than a sign-in form
 * has any business being.
 */
export const DIALOG_PAPER_WIDTH: Partial<Record<DialogType, number>> = {
  [DIALOG_NAMES.AUTH]: 860,
  // The wheel at its largest, 520px, with room either side.
  [DIALOG_NAMES.DAILY_SPIN]: 560,
};

export const NON_FULLSCREEN_DIALOGS = new Set<DialogType>([DIALOG_NAMES.AUTH]);

/** Dialogs drawn straight on the backdrop, with no panel behind them. */
export const BARE_DIALOGS = new Set<DialogType>([DIALOG_NAMES.DAILY_SPIN]);
